import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import { updateProjectSetting } from 'features/project';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import {
  applyNoExistingEquipmentAnswer,
  REMOVE_EXISTING_EQUIPMENT_CANCEL_BUTTON,
  REMOVE_EXISTING_EQUIPMENT_CONFIRM_BUTTON,
  REMOVE_EXISTING_EQUIPMENT_CONFIRM_MESSAGE,
  REMOVE_EXISTING_EQUIPMENT_CONFIRM_TITLE
} from 'utils/apply-no-existing-equipment-answer';
import {
  applyNoExistingPtacAnswer,
  hasExistingPtacAnswers,
  REMOVE_EXISTING_PTAC_CONFIRM_BUTTON,
  REMOVE_EXISTING_PTAC_CONFIRM_MESSAGE,
  REMOVE_EXISTING_PTAC_CONFIRM_TITLE
} from 'utils/apply-no-existing-ptac-answer';
import { isPtacExistingSystem } from 'utils/equipment-preferences-complete';
import {
  HAS_EXISTING_EQUIPMENT_NO,
  HAS_EXISTING_EQUIPMENT_YES
} from 'utils/existing-equipment-page-visibility';
import {
  EXISTING_EQUIPMENT_QUESTION_PROMPT_STRUCTURE_FALLBACK,
  existingEquipmentQuestionPromptText,
  formatExistingEquipmentQuestionPrompt
} from 'utils/existing-equipment-question-prompt';
import { shouldConfirmExistingEquipmentRemoval } from 'utils/should-confirm-existing-equipment-removal';
import { confirmationModal } from 'utils/ui';

export default function HasExistingEquipmentStep() {
  const dispatch = useAppDispatch();
  const projectType = useAppSelector(
    (state) =>
      state.project.projectSettings.find((ps) => ps.code === 'project_type')
        ?.value
  );
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const structureSubtypeLabel = useAppSelector((state) => {
    const structureType = state.data.structureTypes.find(
      (type) => type.id === state.structure.structureTypeId
    );
    const structureSubtype = structureType?.subtypes.find(
      (subtype) => subtype.id === state.structure.structureSubtypeId
    );
    return (
      structureSubtype?.label ??
      EXISTING_EQUIPMENT_QUESTION_PROMPT_STRUCTURE_FALLBACK
    );
  });
  const isPtac = useIsPtacPathway();
  const promptText = existingEquipmentQuestionPromptText(isPtac);
  const promptTemplate = useAppSelector(
    (state) => state.data.textKeys[promptText.key] ?? promptText.fallback
  );
  const prompt = formatExistingEquipmentQuestionPrompt(
    promptTemplate,
    structureSubtypeLabel
  );
  const ptacAnchor = existingSystems.find(isPtacExistingSystem) ?? null;

  const selectYes = () => {
    dispatch(
      updateProjectSetting({
        attributeCode: 'project_type',
        value: HAS_EXISTING_EQUIPMENT_YES
      })
    );
  };

  const selectNoForPtac = () => {
    const anchor = ptacAnchor
      ? {
          systemUniqueId: ptacAnchor.uniqueId,
          entry: recommendationSelectionsBySystem[ptacAnchor.uniqueId]
        }
      : null;
    if (hasExistingPtacAnswers(anchor?.entry?.ptac?.existing)) {
      confirmationModal(
        REMOVE_EXISTING_PTAC_CONFIRM_MESSAGE,
        () => applyNoExistingPtacAnswer(dispatch, anchor),
        REMOVE_EXISTING_PTAC_CONFIRM_TITLE,
        REMOVE_EXISTING_EQUIPMENT_CANCEL_BUTTON,
        REMOVE_EXISTING_PTAC_CONFIRM_BUTTON,
        true
      );
      return;
    }
    applyNoExistingPtacAnswer(dispatch, anchor);
  };

  const selectNo = () => {
    if (projectType === HAS_EXISTING_EQUIPMENT_NO) {
      return;
    }

    if (isPtac) {
      selectNoForPtac();
      return;
    }

    if (
      shouldConfirmExistingEquipmentRemoval(
        existingSystems,
        recommendationSelectionsBySystem,
        wantCompositionPicksBySystem
      )
    ) {
      confirmationModal(
        REMOVE_EXISTING_EQUIPMENT_CONFIRM_MESSAGE,
        () => applyNoExistingEquipmentAnswer(dispatch),
        REMOVE_EXISTING_EQUIPMENT_CONFIRM_TITLE,
        REMOVE_EXISTING_EQUIPMENT_CANCEL_BUTTON,
        REMOVE_EXISTING_EQUIPMENT_CONFIRM_BUTTON,
        true
      );
      return;
    }

    applyNoExistingEquipmentAnswer(dispatch);
  };

  return (
    <div id='project-has-existing-equipment' className='flex flex-col'>
      <span className='mb-2 block text-sm font-medium text-gray-700'>
        {prompt}
      </span>
      <div className='flex items-center gap-5'>
        <div className='relative flex min-w-[150px] grow flex-col items-center'>
          <Button
            text='Yes'
            color={
              projectType === HAS_EXISTING_EQUIPMENT_YES ? 'secondary' : 'white'
            }
            size='lg'
            fullWidth
            onClick={selectYes}
          />
        </div>
        <div className='relative flex min-w-[150px] grow flex-col items-center'>
          <Button
            text='No'
            color={
              projectType === HAS_EXISTING_EQUIPMENT_NO ? 'secondary' : 'white'
            }
            size='lg'
            fullWidth
            onClick={selectNo}
          />
        </div>
      </div>
    </div>
  );
}
